import chevronWhite from "../../../Assets/icon/journey/chevron-white.svg";
import chevronBlack from "../../../Assets/icon/journey/chevron-right.svg";
import { MISSION_DIMENSIONS, missionDateLine } from "./evolution";
import { MISSION_SCREEN_COPY as MC } from "./copy";

/**
 * Mission building blocks, styled from the "Your Mission" sheet
 * (Figma 11268:28414) and shared by the sheet, the Mission page, History
 * detail and Review so all four read as one surface.
 */

/** The Mission card's date line: "Created …" / "Evolved …", never "updated". */
export const missionCardDateLine = (mission) =>
  missionDateLine(
    mission?.display_date_kind || "CREATED",
    mission?.display_date || mission?.approved_at || mission?.created_at
  );

/** The gradient MISSION card. `chip` marks an evolved statement on Review. */
export const MissionStatementHero = ({ eyebrow = "MISSION", statement, line, chip }) => (
  <section className="journey-mission-ov__card">
    <div className="journey-mission-ov__card-head">
      <p className="journey-mission-ov__eyebrow">{eyebrow}</p>
      {chip && <span className="journey-mission__evolved-chip journey-mission__evolved-chip--light">{chip}</span>}
    </div>
    <p className="journey-mission-ov__statement">{statement}</p>
    {line && <p className="journey-mission-ov__date">{line}</p>}
    <span className="journey-mission-card__orb" aria-hidden="true" />
  </section>
);

/**
 * The four elements as one bordered list (Identity, Contribution,
 * Relationships, Way of Living — the design's order). `evolved` (a Set of
 * dimension keys) adds the Review screen's "Evolved" chip.
 */
export const DimensionCards = ({ dimensions, evolved, evolvedLabel = "Evolved" }) => {
  const present = MISSION_DIMENSIONS.filter((dim) => dimensions?.[dim.key]);
  if (present.length === 0) return null;
  return (
    <section className="journey-mission-ov__list">
      {present.map((dim) => (
        <div className="journey-mission-ov__item" key={dim.key}>
          <div className="journey-mission-ov__item-head">
            <p className="journey-mission-ov__item-title">{dim.label}</p>
            {evolved?.has(dim.key) && (
              <span className="journey-mission__evolved-chip">{evolvedLabel}</span>
            )}
          </div>
          <p className="journey-mission-ov__item-body">{dimensions[dim.key]}</p>
        </div>
      ))}
    </section>
  );
};

const ActionCard = ({ tone, title, sub, onClick, disabled }) => (
  <button
    type="button"
    className={`journey-mission-ov__action journey-mission-ov__action--${tone}`}
    onClick={onClick}
    disabled={disabled}
  >
    <span className="journey-mission-ov__action-text">
      <span className="journey-mission-ov__action-title">{title}</span>
      <span className="journey-mission-ov__action-sub">{sub}</span>
    </span>
    <span className="journey-mission-ov__action-icon">
      <img src={tone === "primary" ? chevronWhite : chevronBlack} alt="" width="16" height="16" />
    </span>
  </button>
);

/**
 * Evolution entry points under the four elements: a waiting evolved Mission
 * first (nothing changes until it is saved), else an evolution left
 * mid-conversation, else Evolve My Mission; then Mission History when there is
 * more than one Mission state. `evolution` is `GET /journey/mission`'s block.
 */
export const EvolutionActions = ({ evolution, onEvolve, onReview, onHistory, onStop, stopping }) => {
  if (!evolution) return null;
  const draft = evolution.pending_draft;
  const open = evolution.open_evolution_session_id;
  return (
    <>
      {draft?.draft_id ? (
        <ActionCard
          tone="primary"
          title={MC.pendingCta}
          sub={MC.pendingBody}
          onClick={() => onReview(draft.draft_id)}
        />
      ) : (
        evolution.can_evolve && (
          <ActionCard
            tone="primary"
            title={open ? MC.continueEvolving : MC.evolve}
            sub={open ? MC.resumeHint : MC.evolveHint}
            onClick={onEvolve}
          />
        )
      )}
      {open && !draft?.draft_id && onStop && (
        <button
          type="button"
          className="journey-text-btn journey-mission__stop"
          disabled={stopping}
          onClick={() => onStop(open)}
        >
          {MC.stopEvolving}
        </button>
      )}
      {evolution.has_history && (
        <ActionCard tone="soft" title={MC.historyTitle} sub={MC.historySub} onClick={onHistory} />
      )}
    </>
  );
};

/** Card + four elements + evolution actions: the body of "Your Mission". */
export const MissionOverview = ({ mission, evolution, ...actions }) => (
  <div className="journey-mission-ov">
    <MissionStatementHero statement={mission.statement} line={missionCardDateLine(mission)} />
    <DimensionCards dimensions={mission.dimensions} />
    <EvolutionActions evolution={evolution} {...actions} />
  </div>
);
